'use client';

import { useEffect, useMemo, useState } from 'react';
import { useFeatureFlagsStore } from '@/lib/featureFlags';
import { markerFor, readStageHint, type StageCue } from './stage';

/**
 * The cue this tab should show, and whether it is the server's answer.
 *
 * Once the features snapshot is `ready`, the server wins. Before that — and on
 * a load error — the per-origin hint from the last visit stands in: an origin's
 * stage does not change, so a stale hint is still right. localStorage is read
 * in an effect so the static-export prerender and the first client render
 * agree (both null), avoiding a hydration mismatch in StageChip.
 */
export function useStageCue(): { cue: StageCue | null; authoritative: boolean } {
  const environment = useFeatureFlagsStore((s) => s.environment);
  const loadState = useFeatureFlagsStore((s) => s.loadState);
  const [hint, setHint] = useState<StageCue | null>(null);
  useEffect(() => {
    setHint(readStageHint());
  }, []);
  const authoritative = loadState === 'ready';
  const cue = useMemo(
    () => (authoritative ? markerFor(environment) : hint),
    [authoritative, environment, hint],
  );
  return { cue, authoritative };
}
